import axios from "axios";
import API_ENDPOINTS from "../config/apiEndpoints";
import {
  getAccessToken,
  setAccessToken,
  clearAccessToken,
  notifySessionExpired,
} from "./tokenStore";

export const APPLICATION_ID = API_ENDPOINTS.APPLICATION_ID;

const axiosInstance = axios.create({ baseURL: API_ENDPOINTS.BASE_URL, withCredentials: true });
export const axiosInstancePublic = axios.create({
  baseURL: API_ENDPOINTS.BASE_PUBLIC_URL,
});
export const axiosInstanceSecured = axios.create({
  baseURL: API_ENDPOINTS.BASE_AUTH_URL,
  withCredentials: true,
});

const csrfTokens = new Map();
const csrfPromises = new Map();

const normalizeBaseURL = (baseURL) =>
  baseURL.replace(/\/+$/, "");

// export const clearTokens = () => {clearAccessToken(); };
export const clearTokens = () => {
  clearAccessToken();
  clearCsrfToken();
};


const getCsrfToken = async (baseURL) => {
  const normalizedBaseURL = normalizeBaseURL(baseURL);

  const cachedToken = csrfTokens.get(normalizedBaseURL);

  if (cachedToken) {
    return cachedToken;
  }

  const pendingRequest = csrfPromises.get(normalizedBaseURL);

  if (pendingRequest) {
    return pendingRequest;
  }

  const accessToken = getAccessToken();

  const headers = accessToken
    ? { Authorization: `Bearer ${accessToken}` }
    : {};

  const request = axios
    .get(`${normalizedBaseURL}/Security/csrf-token`, {
      withCredentials: true,
      headers,
    })
    .then((response) => {
      const token = response?.data?.token;

      if (!token) {
        throw new Error(
          "CSRF token was not returned by the server."
        );
      }

      csrfTokens.set(normalizedBaseURL, token);

      return token;
    })
    .finally(() => {
      csrfPromises.delete(normalizedBaseURL);
    });

  csrfPromises.set(normalizedBaseURL, request);

  return request;
};


// Clear cached CSRF tokens
export const clearCsrfToken = (baseURL) => {
  if (baseURL) {
    const normalizedBaseURL = normalizeBaseURL(baseURL);

    csrfTokens.delete(normalizedBaseURL);
    csrfPromises.delete(normalizedBaseURL);
  } else {
    csrfTokens.clear();
    csrfPromises.clear();
  }
};


const attachToken = async (config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  const method = config.method?.toUpperCase();

  const isStateChangingRequest = [
    "POST",
    "PUT",
    "PATCH",
    "DELETE",
  ].includes(method);

  if (isStateChangingRequest) {
    const baseURL = config.baseURL;

    if (!baseURL) {
      throw new Error(
        "API base URL is required to fetch the CSRF token."
      );
    }

    const csrfToken = await getCsrfToken(baseURL);

    config.headers = config.headers || {};
    config.headers["X-CSRF-TOKEN"] = csrfToken;
  }

  return config;
};

// axiosInstance.interceptors.request.use(attachToken, (error) =>
//   Promise.reject(error),
// );
// axiosInstanceSecured.interceptors.request.use(attachToken, (error) =>
//   Promise.reject(error),
// );

const isSessionEnded = (error) =>
  error?.sessionInvalid ||
  error?.response?.status === 401 ||
  error?.response?.status === 403;

let refreshPromise = null;

export function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(
        `${API_ENDPOINTS.BASE_AUTH_URL}/${API_ENDPOINTS.REFRESH_TOKEN}`,
        null,
        { withCredentials: true },
      )
      .then(async(response) => {
        const newAccessToken =
          response?.data?.result?.accessToken ?? response?.data?.accessToken;
        if (!newAccessToken) {
          const err = new Error("New access token was not returned.");
          err.sessionInvalid = true;
          throw err;
        }
        setAccessToken(newAccessToken);
        clearCsrfToken(API_ENDPOINTS.BASE_AUTH_URL);
        await getCsrfToken(API_ENDPOINTS.BASE_AUTH_URL);
        return newAccessToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// export function logoutApi() {
//   return axios.post(
//     `${API_ENDPOINTS.BASE_AUTH_URL}/${API_ENDPOINTS.LOGOUT}`,
//     null,
//     { withCredentials: true },
//   );
// }
export async function logoutApi() {
  const baseURL = API_ENDPOINTS.BASE_AUTH_URL;
  const url = `${baseURL}/${API_ENDPOINTS.LOGOUT}`;

  const sendLogout = async () => {
    const csrfToken = await getCsrfToken(baseURL);
    const token = getAccessToken();

    return axiosInstanceSecured.post(url, null, {
      headers: {
        "X-CSRF-TOKEN": csrfToken,
        ...(token
          ? { Authorization: `Bearer ${token}` }
          : {}),
      },
    });
  };

  try {
    return await sendLogout();
  } catch (error) {
    const status = error?.response?.status;

    // Retry once with a fresh CSRF token when validation fails
    if (status === 400 || status === 403) {
      clearCsrfToken(baseURL);
      return await sendLogout();
    }

    if (status === 401) {
      await refreshAccessToken();
      return await sendLogout();
    }

    throw error;
  }
}

const handle401 = (instance) => async (error) => {
  const originalRequest = error?.config;
  const status = error.response?.status;

if (
    status === 401 &&
    originalRequest &&
    !originalRequest._retry
  ) {
    originalRequest._retry = true;

    try {
      const newAccessToken = await refreshAccessToken();
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return instance(originalRequest);
    } catch (refreshError) {
      if (isSessionEnded(refreshError)) {
        console.error("CF Portal session ended:", refreshError);
        clearAccessToken();
        notifySessionExpired();
      }

      return Promise.reject(refreshError?.response?.data ?? refreshError);
    }
  }

  if (error.response?.data) return Promise.reject(error.response.data);
  return Promise.reject(error);
};

// axiosInstance.interceptors.response.use(
//   (response) => response,
//   handle401(axiosInstance),
// );
// axiosInstanceSecured.interceptors.response.use(
//   (response) => response,
//   handle401(axiosInstanceSecured),
// );
const secure = (instance) => {
  instance.interceptors.request.use(
    attachToken,
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    handle401(instance)
  );
};

secure(axiosInstance);
secure(axiosInstanceSecured);


export default axiosInstance;
